/**
 * Copyright (c) 2022 Animation Studio Voronezh.
 *
 * CONFIDENTIAL AND PROPRIETARY
 *
 * This work is provided "AS IS" and subject to the Prodtrack
 * Source Code License. By accessing, using, copying or modifying
 * this work you indicate your agreement to the Prodtrack Source Code License.
 * All rights not expressly granted therein are reserved by Animation Studio Voronezh.
 */

import getFormat from "functions/getFileFormat";
import { CrudApi } from "./crudApi";
import {
    SCENES,
    ASSETS,
    EPISODES,
    SERIES,
    VERSIONS,
} from "functions/entityTypes";

class VersionsApi extends CrudApi {
    constructor() {
        super(VERSIONS);
    }

    reviewFetch(id) {
        const params = {
            include: [
                "status.*",
                "uploaded_file.*",
                "artist.*",
                "link.*",
                "note_links",
                "project",
            ],
        };
        return this.getById(id, { params });
    }

    fetchById(id) {
        const params = {
            include: [
                "artist.*",
                "uploaded_file.*",
                "status.*",
                "task.*",
                "link.*",
                "tags.*",
                "created_by.*",
                "updated_by.*",
                "project.*",
                "visible_to_freelancers.*",
                "published_files.*",
                "note_links.*",
                "task.tags.*",
                "task.created_by.*",
                "task.updated_by.*",
                "task.assigned_to.*",
                "task.status.*",
                "task.pipeline_step.*",
                "custom_field_values.*",
            ],
            "filter{trash}": false,
        };

        return this.getById(id, { params });
    }

    fetchByLink({ id, type }, additionalParams = {}) {
        const linkFilter =
            type === "task"
                ? { "filter{task}": id }
                : { [`filter{link.${type}.id}`]: id };
        const params = {
            include: [
                "artist.*",
                "uploaded_file.*",
                "status.*",
                "task.*",
                "link.*",
                "tags.*",
                "created_by.*",
                "updated_by.*",
                "project.*",
                "visible_to_freelancers.*",
                "published_files.*",
                "note_links.*",
                "task.tags.*",
                "task.created_by.*",
                "task.updated_by.*",
                "task.assigned_to.*",
                "task.status.*",
                "task.pipeline_step.*",
                "custom_field_values.*",
                "artist.extendedfields.*",
            ],
            "filter{trash}": false,
            ...linkFilter,
            ...additionalParams,
        };

        return this.get({ params });
    }

    breadcrumbsFetch(id) {
        return this.getById(id, {
            params: {
                include: ["link.*"],
            },
        }).then(({ data }) => data);
    }

    fetchLatest = (entityType, projectId, ids) =>
        [SERIES, EPISODES, SCENES, ASSETS].includes(entityType)
            ? this.request
                  .post(`/api/v1/${entityType}_versions/${projectId}/`, {
                      ids,
                  })
                  .then(({ data }) => {
                      for (let value of Object.values(data))
                          value.format = getFormat(value.file_type);

                      return data;
                  })
            : Promise.resolve({});

    fetchLatestForSeries = (...args) => this.fetchLatest(SERIES, ...args);
    fetchLatestForEpisodes = (...args) => this.fetchLatest(EPISODES, ...args);
    fetchLatestForScenes = (...args) => this.fetchLatest(SCENES, ...args);
    fetchLatestForAssets = (...args) => this.fetchLatest(ASSETS, ...args);
}

export const versionsApi = new VersionsApi();
